import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFinancialSummaryTranslations } from '@common/i18n/financialSummary';
import type {
    BlockQuestion,
    CurrencyValueType,
    QuestionSchema,
    TemplateFormField,
} from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { getContactBlockQuestions, getFilledContactBlockEntries } from '@common/utils/contactBlock';
import { getVisibleQuestions } from '@common/utils/questionVisibility';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { toFormValueString } from './types';

function formatCurrency(value: number, language: TemplateFormLanguage) {
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return `$${value.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function parseCurrencyValue(value: unknown): number {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value !== 'string') return 0;

    const numericValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
    return Number.isNaN(numericValue) ? 0 : numericValue;
}

function parseTableRows(value: unknown): Record<string, unknown>[] {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.filter(
            (row): row is Record<string, unknown> =>
                !!row && typeof row === 'object' && !Array.isArray(row),
        );
    }
    if (typeof value !== 'string') return [];

    try {
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed)) return [];

        return parsed.filter(
            (row): row is Record<string, unknown> =>
                !!row && typeof row === 'object' && !Array.isArray(row),
        );
    } catch {
        return [];
    }
}

interface Props {
    groupedFields: Record<string, TemplateFormField[]>;
    formValues: Record<string, string | null>;
    language?: TemplateFormLanguage;
}

const MONO_FONT =
    '"Roboto Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

export default function FinancialSummary({
    groupedFields,
    formValues,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const t = getFinancialSummaryTranslations(language);
    const { totalAssets, totalDebts, netWorth } = useMemo(() => {
        const allFields = Object.values(groupedFields).flat();
        let totalAssets = 0;
        let totalDebts = 0;

        const addAmount = (value: unknown, valueType: CurrencyValueType | undefined) => {
            if (valueType === 'asset') {
                totalAssets += parseCurrencyValue(value);
            } else if (valueType === 'debt') {
                totalDebts += parseCurrencyValue(value);
            }
        };

        const addQuestionTotals = (schema: QuestionSchema, value: unknown) => {
            if (schema.type === 'currency') {
                addAmount(value, schema.valueType);
            }

            if (schema.type === 'table') {
                const currencyColumns =
                    schema.columns?.filter((column) => column.type === 'currency') ?? [];
                for (const row of parseTableRows(value)) {
                    for (const column of currencyColumns) {
                        addAmount(row[column.key], column.valueType);
                    }
                }
            }

            if (schema.type === 'contactInfoSection') {
                const questions = getContactBlockQuestions(schema);
                for (const entry of getFilledContactBlockEntries(value, schema)) {
                    const getAnswer = (question: BlockQuestion) =>
                        entry.answers[question.questionSchema.name] ??
                        toFormValueString(question.questionSchema.value);
                    for (const question of getVisibleQuestions(questions, getAnswer)) {
                        addQuestionTotals(question.questionSchema, getAnswer(question));
                    }
                }
            }
        };

        for (const field of allFields) {
            addQuestionTotals(
                field.questionSchema,
                formValues[field.templateFormFieldId] ?? field.questionSchema.value,
            );
        }

        return {
            totalAssets,
            totalDebts,
            netWorth: totalAssets - totalDebts,
        };
    }, [groupedFields, formValues]);

    return (
        <Stack
            sx={{
                p: isMobile ? 2 : 3,
                borderRadius: '8px',
                backgroundColor: theme.palette.neutral.white,
                border: `1px solid ${theme.palette.neutral.borderColor}`,
                gap: 2,
            }}
        >
            <Typography variant="h3">{t.title}</Typography>
            <Stack
                sx={{
                    borderBottom: `1px solid ${theme.palette.neutral.borderColor}`,
                    pb: '10px',
                    gap: isMobile ? 0.5 : 3,
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    width: '100%',
                }}
            >
                <Typography variant="body2" sx={{ flex: isMobile ? '1 1 100%' : 1, minWidth: 0 }}>
                    {t.totalAssets}
                </Typography>
                <Typography
                    variant="h4"
                    sx={{
                        flex: isMobile ? '1 1 100%' : '0 0 auto',
                        fontWeight: 500,
                        lineHeight: '16px',
                        fontFamily: MONO_FONT,
                        textAlign: isMobile ? 'left' : 'right',
                        ml: isMobile ? 0 : 'auto',
                    }}
                >
                    {formatCurrency(totalAssets, language)}
                </Typography>
            </Stack>
            <Stack
                sx={{
                    borderBottom: `1px solid ${theme.palette.neutral.borderColor}`,
                    pb: '10px',
                    gap: isMobile ? 0.5 : 3,
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    width: '100%',
                }}
            >
                <Typography variant="body2" sx={{ flex: isMobile ? '1 1 100%' : 1, minWidth: 0 }}>
                    {t.totalDebts}
                </Typography>
                <Typography
                    variant="h4"
                    sx={{
                        flex: isMobile ? '1 1 100%' : '0 0 auto',
                        fontWeight: 500,
                        lineHeight: '16px',
                        fontFamily: MONO_FONT,
                        textAlign: isMobile ? 'left' : 'right',
                        ml: isMobile ? 0 : 'auto',
                    }}
                >
                    {formatCurrency(totalDebts, language)}
                </Typography>
            </Stack>
            <Stack
                sx={{
                    gap: isMobile ? 0.5 : 3,
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    width: '100%',
                }}
            >
                <Typography variant="body2" sx={{ flex: isMobile ? '1 1 100%' : 1, minWidth: 0 }}>
                    {t.netWorth}
                </Typography>
                <Typography
                    variant="h4"
                    sx={{
                        flex: isMobile ? '1 1 100%' : '0 0 auto',
                        fontWeight: 600,
                        lineHeight: '16px',
                        fontFamily: MONO_FONT,
                        color: netWorth >= 0 ? 'success.main' : 'error.main',
                        textAlign: isMobile ? 'left' : 'right',
                        ml: isMobile ? 0 : 'auto',
                    }}
                >
                    {formatCurrency(netWorth, language)}
                </Typography>
            </Stack>
        </Stack>
    );
}
